import {
  type ValueFilter,
  type PublishedPayableRule,
} from '@spendesk/bookkeeping-core-types';
import { type nonEmptyArray as NEA } from '@spendesk/general-type-helpers';

import {
  type UncheckedQueryState,
  usePromiseToQuery,
} from 'src/core/api/hooks/usePromiseToQuery';
import {
  type TGlobalFunctionTyped,
  useTranslation,
} from 'src/core/common/hooks/useTranslation';
import {
  useLoadExpenseAccountsByIds,
  useLoadMembersByIds,
  useLoadTaxAccountsByIds,
} from 'src/core/modules/bookkeep/prepare-payables/components/PreparePayablesFiltersBar/components/PreparePayablesFiltersAdditionalFilters/hooks';
import { useLoadCustomFieldsValuesByIdsAsPromise } from 'src/core/modules/bookkeep/prepare-payables/components/PreparePayablesFiltersBar/components/PreparePayablesFiltersAdditionalFilters/hooks/useLoadCustomFieldValuesByIds';
import { type CustomField } from 'src/core/modules/bookkeep/prepare-payables/models';
import {
  getCustomFieldIdsToFetch,
  getCustomFieldValuesIdsToFetch,
  getPayablesExpenseAccountIdsToFetch,
  getPayablesSupplierIdsToFetch,
  getPayablesVatAccountIdsToFetch,
  getPayableUserIdsToFetch,
} from 'src/core/modules/bookkeep/settings/integrations/pages/PayableRulesSetupPage/models/payableRules';
import { useCostCentersQuery } from 'src/core/modules/budgets/apis';
import { useLoadSuppliersByIds } from 'src/core/modules/suppliers/hooks';
import { logger } from 'src/utils/datadog-log-wrapper';

export type EnrichedPayableRule<
  Rule extends PublishedPayableRule = PublishedPayableRule,
> = Omit<Rule, 'conditions' | 'outputs'> & {
  displayName: string;
  conditions: EnrichedCondition[];
  outputs: EnrichedOutputs;
  rawConditions: Rule['conditions'];
  rawOutputs: Rule['outputs'];
  createdByUser?: Member;
  updatedByUser?: Member;
  problems: string[];
  stats?: Stats;
};

export type Stats = {
  ruleId: string;
  payablesMatched: number;
  payablesPrepared: number;
};

type Member = Awaited<
  ReturnType<ReturnType<typeof useLoadMembersByIds>>
>[number];

export function useEnrichedPayableAutomationRulesQuery<
  Rule extends PublishedPayableRule = PublishedPayableRule,
>(rules: Rule[]): UncheckedQueryState<EnrichedPayableRule<Rule>[]> {
  const { t } = useTranslation('global');
  const queries = useQueries(rules);

  const error = Object.values(queries).find(
    (query) => query.status === 'error',
  );
  if (error) {
    return error;
  }

  // For queries that are loading, we just consider them loaded but empty.
  // This way we can partially enrich the rules.
  const loaded = Object.fromEntries(
    Object.entries(queries).map(([name, query]) => [
      name,
      query.status === 'loading' ? { status: 'success', data: [] } : query,
    ]),
  ) as SuccessfulQueries;

  const enrichedRules: EnrichedPayableRule<Rule>[] = rules.map((rule) => ({
    ...rule,
    rawConditions: rule.conditions,
    rawOutputs: rule.outputs,
    conditions: enrichConditions(rule.conditions, loaded),
    outputs: enrichOutputs(rule.outputs, loaded),
    createdByUser: rule.createdByUserId
      ? loaded.users.data.find(({ id }) => id === rule.createdByUserId)
      : undefined,
    updatedByUser: rule.updatedByUserId
      ? loaded.users.data.find(({ id }) => id === rule.updatedByUserId)
      : undefined,
    problems: [],
    displayName: buildDisplayName(rule, t),
  }));

  enrichedRules.forEach((rule) => {
    if (
      rule.outputs?.expenseAccount &&
      typeof rule.outputs?.expenseAccount !== 'string' &&
      rule.outputs?.expenseAccount.isArchived
    ) {
      rule.problems.push(
        t(
          'bookkeep.integrations.settings.payableRules.errors.outputs.missingOutput',
        ),
      );
    }
    // If some of the custom fields is archived, we consider the rule as having a problem
    const customFieldsWithArchiveStatus = getCustomFieldsWithArchiveStatus(
      rule.outputs?.customField,
    );
    if (
      customFieldsWithArchiveStatus.some(
        (customField) => customField.isArchived,
      )
    ) {
      rule.problems.push(
        t(
          'bookkeep.integrations.settings.payableRules.errors.outputs.missingOutput',
        ),
      );
    }

    // If some of cost centers is archived, we consider the rule as having a problem
    if (
      rule.outputs?.costCenter &&
      typeof rule.outputs?.costCenter !== 'string' &&
      rule.outputs?.costCenter.isDeleted
    ) {
      rule.problems.push(
        t(
          'bookkeep.integrations.settings.payableRules.errors.outputs.missingOutput',
        ),
      );
    }

    if (
      rule.outputs?.vatAccount &&
      typeof rule.outputs?.vatAccount !== 'string' &&
      rule.outputs?.vatAccount.isArchived
    ) {
      rule.problems.push(
        t(
          'bookkeep.integrations.settings.payableRules.errors.outputs.missingOutput',
        ),
      );
    }

    // If some of the conditions are archived, we consider the rule as having a problem
    if (
      rule.conditions.some((condition) => {
        const isCustomFieldArchived =
          condition.field === 'customField' &&
          'customField' in condition.value &&
          condition.value.customField.archiveDate;

        const isCustomFieldValueArchived =
          condition.field === 'customField' &&
          typeof condition.value === 'object' &&
          Array.isArray(condition.value.value) &&
          condition.value.value.some(
            (value) =>
              typeof value === 'object' &&
              value !== null &&
              'archiveDate' in value &&
              value.archiveDate,
          );

        const isCostCenterArchived =
          condition.field === 'costCenter' &&
          Array.isArray(condition.value) &&
          condition.value.some(
            (costCenterValue) =>
              typeof costCenterValue === 'object' &&
              costCenterValue !== null &&
              'isDeleted' in costCenterValue &&
              costCenterValue.isDeleted,
          );
        const isSupplierArchived =
          condition.field === 'supplier' &&
          Array.isArray(condition.value) &&
          condition.value.some(
            (supplierValue) =>
              typeof supplierValue === 'object' &&
              supplierValue !== null &&
              'isArchived' in supplierValue &&
              supplierValue.isArchived,
          );
        const isRequestorArchived =
          condition.field === 'requestor' &&
          Array.isArray(condition.value) &&
          condition.value.some(
            (requestorValue) =>
              typeof requestorValue === 'object' &&
              requestorValue !== null &&
              'isDeleted' in requestorValue &&
              requestorValue.isDeleted,
          );
        return (
          isCustomFieldValueArchived ||
          isCustomFieldArchived ||
          isCostCenterArchived ||
          isSupplierArchived ||
          isRequestorArchived
        );
      })
    ) {
      rule.problems.push(
        t(
          'bookkeep.integrations.settings.payableRules.errors.conditions.missingCondition',
        ),
      );
    }
  });

  const sortedEnrichedRules = enrichedRules.sort((a, b) =>
    a.createdAt > b.createdAt ? -1 : 1,
  );
  return { status: 'success', data: sortedEnrichedRules };
}

function useQueries(rules: PublishedPayableRule[]) {
  const loadMembersByIds = useLoadMembersByIds();
  const loadSuppliersByIds = useLoadSuppliersByIds();
  const loadExpenseAccountsByIds = useLoadExpenseAccountsByIds(true);
  const loadTaxAccountsByIds = useLoadTaxAccountsByIds(true);
  const loadCustomFieldValuesQuery = useLoadCustomFieldsValuesByIdsAsPromise();

  const userIds = getPayableUserIdsToFetch(rules);
  const supplierIds = getPayablesSupplierIdsToFetch(rules);
  const expenseAccountIds = getPayablesExpenseAccountIdsToFetch(rules);
  const taxAccountIds = getPayablesVatAccountIdsToFetch(rules);
  const customFieldIds = getCustomFieldIdsToFetch(rules);
  const customFieldValueIds = getCustomFieldValuesIdsToFetch(rules);

  return {
    users: usePromiseToQuery(
      () => loadMembersByIds(userIds),
      {
        isEnabled: userIds.length > 0,
      },
      userIds,
    ),

    suppliers: usePromiseToQuery(
      () => loadSuppliersByIds(supplierIds),
      {
        isEnabled: supplierIds.length > 0,
      },
      supplierIds,
    ),

    expenseAccounts: usePromiseToQuery(
      () => loadExpenseAccountsByIds(expenseAccountIds),
      { isEnabled: expenseAccountIds.length > 0 },
      expenseAccountIds,
    ),

    vatAccounts: usePromiseToQuery(
      () => loadTaxAccountsByIds(taxAccountIds),
      {
        isEnabled: taxAccountIds.length > 0,
      },
      taxAccountIds,
    ),

    costCenters: useCostCentersQuery({ withDeleted: true }),

    customFields: usePromiseToQuery(
      () => loadCustomFieldValuesQuery({ customFieldIds, customFieldValueIds }),
      {
        isEnabled: customFieldIds.length > 0,
      },
      customFieldIds.concat(customFieldValueIds),
    ),
  };
}

type Queries = ReturnType<typeof useQueries>;
type SuccessfulQueries = {
  [K in keyof Queries]: Queries[K] & { status: 'success' };
};

export type EnrichedCondition =
  | (ValueFilter & {
      field: 'payableType' | 'grossAmount';
    })
  | EnrichedValueFilter<'supplier' | 'requestor' | 'costCenter'>
  | EnrichedCustomFieldFilter;

function enrichConditions(
  conditions: PublishedPayableRule['conditions'],
  queries: SuccessfulQueries,
): EnrichedCondition[] {
  return conditions.map((condition) => enrichCondition(condition, queries));
}

function enrichCondition(
  condition: PublishedPayableRule['conditions'][number],
  queries: SuccessfulQueries,
): EnrichedCondition {
  if (condition.field === 'customField') {
    return enrichCustomFieldCondition(condition, queries);
  }

  if (
    condition.field === 'payableType' ||
    condition.field === 'grossAmount' ||
    !Array.isArray(condition.value)
  ) {
    return condition as EnrichedCondition;
  }

  switch (condition.field) {
    case 'requestor':
      return {
        ...condition,
        value: enrichIds(condition.value, queries.users.data),
      };
    case 'supplier':
      return {
        ...condition,
        value: enrichIds(condition.value, queries.suppliers.data),
      };
    case 'costCenter':
      return {
        ...condition,
        value: enrichIds(condition.value, queries.costCenters.data),
      };
    default:
      return condition as EnrichedCondition;
  }
}

function enrichCustomFieldCondition(
  condition: PublishedPayableRule['conditions'][number] & {
    field: 'customField';
  },
  queries: SuccessfulQueries,
): EnrichedCustomFieldFilter {
  const { key: customFieldId, value } = condition.value;

  const customField = queries.customFields.data.find(
    ({ id }) => customFieldId === id,
  );
  if (!customField) {
    return condition;
  }

  return {
    ...condition,
    value: {
      customField,
      value: !Array.isArray(value)
        ? value
        : enrichIds(value, customField.values),
    },
  };
}

function enrichIds<E extends { id: string }>(
  value: NEA.NonEmptyArray<string>,
  source: E[],
): NEA.NonEmptyArray<E | string> {
  // Can't use NEA.map because general-type-helpers has runtime dependencies on Node :(
  return value.map((id) => enrichId(id, source)) as NEA.NonEmptyArray<
    E | string
  >;
}

function enrichId<E extends { id: string }>(
  id: string,
  source: E[],
): E | string {
  return source.find((entity) => entity.id === id) ?? id;
}

export type EnrichedValueFilter<
  Field extends IdValueFilter['field'] & keyof Enriched,
> = Omit<IdValueFilter & { field: Field }, 'value'> & {
  value: null | 'any' | NEA.NonEmptyArray<Enriched[Field] | string>;
};

type Enriched = {
  requestor: SuccessfulQueries['users']['data'][number];
  supplier: SuccessfulQueries['suppliers']['data'][number];
  expenseAccount: SuccessfulQueries['expenseAccounts']['data'][number];
  vatAccount: SuccessfulQueries['vatAccounts']['data'][number];
  costCenter: SuccessfulQueries['costCenters']['data'][number];
};

type IdValueFilter = ValueFilter & {
  operator: '=' | '!=';
  value: null | 'any' | NEA.NonEmptyArray<string>;
};

type CustomFieldFilter = ValueFilter & { field: 'customField' };

export type EnrichedCustomFieldFilter =
  | CustomFieldFilter
  | (Omit<CustomFieldFilter, 'value'> & {
      value: {
        customField: CustomField;
        value:
          | null
          | 'any'
          | NEA.NonEmptyArray<CustomField['values'][number] | string>;
      };
    });

export type EnrichedOutputs = {
  [K in keyof Enriched & keyof PublishedPayableRule['outputs']]?:
    | Enriched[K]
    | string;
} & {
  customField?: Record<
    string,
    | {
        customField: CustomField;
        value: CustomField['values'][number] | string;
        archiveDate?: string | null;
      }
    | string
    | undefined
  >;
};

function enrichOutputs(
  outputs: PublishedPayableRule['outputs'],
  queries: SuccessfulQueries,
): EnrichedOutputs {
  const enriched: EnrichedOutputs = {};

  if (outputs.costCenter) {
    enriched.costCenter = enrichId(
      outputs.costCenter,
      queries.costCenters.data,
    );
  }

  if (outputs.expenseAccount) {
    enriched.expenseAccount = enrichId(
      outputs.expenseAccount,
      queries.expenseAccounts.data,
    );
  }

  if (outputs.vatAccount) {
    enriched.vatAccount = enrichId(
      outputs.vatAccount,
      queries.vatAccounts.data,
    );
  }

  if (outputs.customField) {
    enriched.customField = Object.fromEntries(
      Object.entries(outputs.customField).map(([customFieldId, valueId]) => {
        const customField = queries.customFields.data.find(
          ({ id }) => id === customFieldId,
        );
        return [
          customFieldId,
          customField
            ? {
                customField,
                value: enrichId(valueId, customField?.values ?? []),
              }
            : customFieldId,
        ];
      }),
    );
  }

  return enriched;
}

export function getCustomFieldsWithArchiveStatus(
  customFieldsObject: EnrichedOutputs['customField'],
) {
  if (!customFieldsObject) {
    return [];
  }
  return Object.values(customFieldsObject).reduce(
    (accumulator, customFieldValue) => {
      if (
        typeof customFieldValue !== 'object' ||
        !customFieldValue.customField
      ) {
        return accumulator;
      }
      const { customField, value, archiveDate } = customFieldValue;
      const customFieldArchiveDate = customField?.archiveDate ?? null;
      let customFieldValueArchiveDate: string | null = null;
      if (value && typeof value === 'object' && 'archiveDate' in value) {
        customFieldValueArchiveDate =
          (value as { archiveDate?: string | null }).archiveDate ?? null;
      }

      const customFieldValueName =
        typeof value === 'object' ? value.name : undefined;

      // Only mark as archived if any archiveDate is a non-empty string
      const isArchived =
        !!customFieldArchiveDate ||
        !!customFieldValueArchiveDate ||
        !!archiveDate;

      if (customField.type === 'Boolean') {
        accumulator.push({
          name: customField.name,
          cfName: customField.name,
          value:
            typeof customFieldValueName === 'string'
              ? customFieldValueName
              : String(customFieldValueName),
          isArchived,
          type: customField.type,
          id: customField.id,
        });
      } else {
        accumulator.push({
          name:
            typeof customFieldValueName === 'string'
              ? customFieldValueName
              : '',
          value:
            typeof customFieldValueName === 'string'
              ? customFieldValueName
              : '',
          cfName: customField.name,
          isArchived,
          type: customField.type,
          id: customField.id,
        });
      }
      return accumulator;
    },
    [] as Array<{
      id: string;
      cfName: string;
      name: string;
      value: string | undefined;
      isArchived: boolean;
      type: string;
    }>,
  );
}

function buildDisplayName(
  rule: PublishedPayableRule,
  t: TGlobalFunctionTyped,
): string {
  if (typeof rule.name === 'string' && rule.name !== '') {
    return rule.name;
  }
  // Assumption: if the rule has no name, it's migrated rule
  // get first condition to figure out if it's supplier or expense category
  const firstCondition = rule.conditions[0];
  if (firstCondition) {
    if (firstCondition.field === 'supplier') {
      return t(
        'bookkeep.integrations.settings.payableRules.defaultSupplierRuleName',
      );
    }
    // ExpenseCategory is a subset of customFields
    if (firstCondition.field === 'customField') {
      return t(
        'bookkeep.integrations.settings.payableRules.defaultExpenseCategoryRuleName',
      );
    }
  }
  logger.warn('Payable rules name was generated with unexpected value', {
    scope: 'payable-rules',
    team: 'finance-accountant',
    rule,
  });
  return t(
    'bookkeep.integrations.settings.payableRules.defaultUnnamedRuleName',
  );
}
